import React, {PureComponent} from 'react'
import PropTypes from 'prop-types'

import Grid from '@material-ui/core/Grid'
import Button from '@material-ui/core/Button'
import AddIcon from '@material-ui/icons/Add'
import CircularProgress from '@material-ui/core/CircularProgress'
import ImageIcon from '@material-ui/icons/Image'
import {Editor} from '@tinymce/tinymce-react'

import {showAlert, showHtmlAlert} from './alert/alert'
import {validateAuth} from '../services/oauthService'

import ApiService from '../services/dataService'

import getString from '../config/strings'
import DocumentUploadDialog from "./documentUploadDialog";
import DocumentTable from "./documentTable";

function shallowDiffers(a, b) {
    for (let i in a) {
        if (!(i in b)) return true
    }
    for (let i in b) {
        if (Array.isArray(a[i])) {
            if (!Array.isArray(b[i])) return true
            if (shallowDiffers(a[i], b[i])) return true
        } else if (a[i] !== null && typeof a[i] === 'object') {
            if (b[i] === null || typeof b[i] !== 'object') return true
            if (shallowDiffers(a[i], b[i])) return true
        } else {
            if (a[i] !== b[i]) return true
        }
    }
    return false
}

const removeEnvelopingParagraph = (text) => {
    if ((text || '').match(/^<p[^>]*>.*<\/p>$/)) {
        const shortenedText = text.substring(text.indexOf('>') + 1, text.lastIndexOf('<'))
        if (!shortenedText.match(/^.*?<\/p>/)) return shortenedText
    }
    return text
}

class CorporateCardPage extends PureComponent {
    constructor(props) {
        super(props)
        this.state = {
            card: {},
            loaded: false,
            dialogOpen: false,
            uploadProgress: false,
            changeCard: false
        }
    }

    initData() {
        const {auth} = this.props

        this.setState({
            loaded: true,
            card: {
                description: getString('CARD_TEXT_INIT'),
                files: [],
                policy_number: auth.user.additionalData.policyNumber,
                logopath: ''
            }
        })
    }

    async fetchCard() {
        const {auth, parent} = this.props

        try {
            const newAuth = await validateAuth(auth)
            if (newAuth === null) {
                parent.setLogin(null)
                return
            }

            const data = await ApiService.getCard(newAuth.access_token)
            if (data === null) {
                // init empty
                this.initData()
            } else {
                if (data.files === null) data.files = []
                this.setState({card: data, loaded: true})
            }

            if (newAuth.access_token !== auth.access_token) {
                parent.setLogin(auth)
            }

        } catch (error) {
            if (error.message.match('Not found')) {
                // init empty
                this.initData()
            } else {
                console.error('ERROR', 'component mount', error)
                showAlert(getString(error.message), getString('ERROR_HEADLINE'))
            }
        }
    }

    componentDidMount() {
        this.fetchCard().then(null).catch(console.error)
    }

    async saveCard(card) {
        const {auth, parent} = this.props

        try {
            const newAuth = await validateAuth(auth)
            if (newAuth === null) {
                parent.setLogin(null)
                return
            }

            const data = await ApiService.setCard(card, newAuth.access_token)
            // store reponse after POST with id
            if ((data.id && card.id !== data.id) ||
                (data.policy_number && card.policy_number !== data.policy_number) ||
                (data.logopath && card.logopath !== data.logopath) ||
                (data.logo && card.logo !== data.logo)) {
                let newRecord = {...card}
                if (data.id) newRecord.id = data.id
                if (data.policy_number) newRecord.policy_number = data.policy_number
                if (data.logopath) newRecord.logopath = data.logopath
                if (data.logo) newRecord.logo = data.logo
                this.setState({card: newRecord, changeCard: false})
            } else {
                this.setState({changeCard: false})
            }

            if (newAuth.access_token !== auth.access_token) {
                parent.setLogin(auth)
            }
        } catch (error) {
            console.error('ERROR', 'component update', error)
            showAlert(getString(error.message), getString('ERROR_HEADLINE'))
        }
    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        const {card} = this.state
        if (!shallowDiffers(prevState.card, card)) return

        this.saveCard(card).then(null).catch(console.error)
    }

    openDocumentDialog(index) {
        const {card} = this.state
        const record = (index !== -1 && card.files && card.files[index])
            ? Object.assign({}, card.files[index])
            : {card_id: card.id, title: '', description: '', filepath: '', fileorder: -1}

        this.setState({dialogOpen: true, actRecord: record, actIndex: index})
    }

    closeDocumentDialog() {
        this.setState({dialogOpen: false})
    }

    saveDocumentRecord(record) {
        const {card, actIndex} = this.state

        if (record.title && record.filepath) {
            record.description = removeEnvelopingParagraph(record.description)

            const newCard = {...card}
            if (card.files) newCard.files = card.files.slice(0)
            if (newCard.files === null) newCard.files = []

            if (actIndex === -1) {
                newCard.files.push(record)
                record.fileorder = newCard.files.length
            } else {
                if (newCard.files.length > actIndex) {
                    newCard.files[actIndex] = record
                    record.fileorder = actIndex + 1
                } else {
                    newCard.files.push(record)
                    record.fileorder = newCard.files.length
                }
            }

            this.setState({card: newCard, dialogOpen: false})
        } else {
            showAlert(getString('invalid inputs'), getString('ERROR_HEADLINE'))
        }
    }

    deleteDocumentRecord(index) {
        const {card} = this.state
        const record = (card.files[index]) ? {...card.files[index]} : null
        if (!record) return

        const newCard = {...card}
        if (card.files) {
            newCard.files = card.files.slice(0)
        }
        for (let i = 0, l = newCard.files.length; i < l; i++) {
            if (newCard.files[i].id === record.id) {
                newCard.files.splice(i, 1)
                break
            }
        }
        this.setState({card: newCard})
    }

    askDocumentDelete(index) {
        const {card} = this.state
        const record = (card.files[index]) ? {...card.files[index]} : null
        if (!record) return

        const self = this
        showHtmlAlert(getString('DELETE_DOCUMENT_COPY', [record.title]),
            getString('DELETE_DOCUMENT_HEADER'),
            [{
                label: getString('YES'), callback: () => {
                    self.deleteDocumentRecord(index)
                }
            },
                {label: getString('NO')}])
    }

    renderDocumentTable() {
        const {card} = this.state

        return (<DocumentTable
            card={card}
            openDialog={(index) => this.openDocumentDialog(index)}
            askDelete={(index) => this.askDocumentDelete(index)}
        />)
    }

    renderDocumentDialog() {
        const {actIndex, actRecord} = this.state
        const {auth, parent} = this.props

        return (<DocumentUploadDialog
            auth={auth}
            parent={parent}
            closeFunc={() => {
                this.closeDocumentDialog()
            }}
            title={getString(actIndex === -1 ? 'ADD_DOCUMENT' : 'EDIT_DOCUMENT')}
            action={getString(actIndex === -1 ? 'SAVE_DOCUMENT' : 'UPDATE_DOCUMENT')}
            record={actRecord}
            saveFunc={(record) => this.saveDocumentRecord(record)}
        />)
    }

    changeCardField(fieldName, value) {
        let newRecord = {...this.state.card}
        if (fieldName === 'description') {
            value = value.replace(/(^\s+$)/, '')
            value = removeEnvelopingParagraph(value)
        }

        if (value !== newRecord[fieldName]) {
            newRecord[fieldName] = value
            this.setState({card: newRecord, changeCard: true})
        }
    }

    async uploadCardLogo(event) {
        const {uploadProgress} = this.state
        if (uploadProgress) return

        let fileinput = event.target
        if (fileinput.files.length <= 0 || !fileinput.files[0].name) return

        const {auth, parent} = this.props

        this.setState({uploadProgress: true})
        try {
            const newAuth = await validateAuth(auth)
            if (newAuth === null) {
                parent.setLogin(null)
                return
            }

            const data = await ApiService.uploadFile(fileinput.form, newAuth.access_token)
            if (data.relativePath) {
                this.changeCardField('logopath', data.relativePath)
                this.setState({uploadProgress: false})
            }

            if (newAuth.access_token !== auth.access_token) {
                parent.setLogin(auth)
            }
        } catch (error) {
            this.setState({uploadProgress: false})
            console.error('ERROR', 'logo failed', error)
            showAlert(getString(error.message), getString('ERROR_HEADLINE'))
        }
    }

    render() {
        const {auth} = this.props
        const {loaded, card, uploadProgress, dialogOpen, changeCard} = this.state

        return (
            <div style={{padding: 12, maxWidth: '720px', margin: '0 auto'}}>
                {loaded &&
                    <Grid container spacing={3} style={{flexGrow: 1}}>
                        <Grid item xs={12}>
                            <p style={{color: 'gray', fontSize: '85%', margin: 0}}>{getString('SAVING_HINT')}</p>
                        </Grid>
                        <Grid item xs={12}>
                            <h3 className="fieldSet">{getString('INTRODUCTION_HEADER')}</h3>
                        </Grid>

                        <Grid item xs={12}>
                            <form action={'/rest/v1/uploadFile/' + auth.user.additionalData.policyNumber} method="POST"
                                  encType="multipart/form-data">
                                {card.logo &&
                                    <img src={card.logo} style={{verticalAlign: 'top', maxWidth: 320}}
                                         alt="your logo"/>}
                                {!card.logopath &&
                                    <ImageIcon nativeColor="#bfbfbf"
                                               style={{width: 160, height: 160, verticalAlign: 'top'}}/>}

                                <Button component='span' variant="contained" color="primary" disabled={uploadProgress}
                                        onClick={(event) => {
                                            event.target.form.querySelector('input').click()
                                        }}
                                        style={{
                                            marginLeft: 24,
                                            marginTop: 12,
                                            verticalAlign: 'top',
                                            position: 'relative'
                                        }}>
                                    <span>Change Logo</span>
                                    <input type="file" name="file"
                                           accept=".png,.jpg,.gif,image/png,image/jpeg,image/gif"
                                           className="fileUploadButton"
                                           onClick={(event) => {
                                               event.stopPropagation()
                                           }}
                                           onChange={(event) => {
                                               this.uploadCardLogo(event).then(null).catch(console.error)
                                           }}/>
                                    {uploadProgress && !dialogOpen && <CircularProgress color="secondary"
                                                                                        style={{
                                                                                            position: 'absolute',
                                                                                            left: '50%',
                                                                                            top: '50%',
                                                                                            transform: 'translate(-50%,-50%)'
                                                                                        }}/>}
                                </Button>
                            </form>
                        </Grid>

                        <Grid item xs={12}>
                            <form onSubmit={(event) => {
                                event.preventDefault()
                                return false
                            }}>
                                <Editor
                                    value={card.description}
                                    label={getString('INSURANCE_DESCRIPTION')}
                                    id="description"
                                    init={{
                                        plugins: 'paste hr lists preview',
                                        branding: false,
                                        menubar: false,
                                        resize: 'both',
                                        theme: 'modern',
                                        min_height: 240,
                                        paste_data_images: false,
                                        paste_word_valid_elements: 'b,strong,i,em,p,ul,li,ol,br',
                                        paste_preprocess: function (plugin, args) {
                                            args.content = args.content.replace(/<\/?(div|h1|h2|h3|h4|h5|span|font)[^>]*>/gi, '')
                                        },
                                        block_formats: 'Absatz=p',
                                        toolbar: 'undo redo | bold italic underline | alignleft aligncenter alignright alignjustify | bullist numlist hr | outdent indent | preview',
                                        content_css: 'stylesheets/ccards_content.css',
                                        entity_encoding: 'raw'
                                    }}
                                    onEditorChange={(text, _editor) => {
                                        this.changeCardField('description', text)
                                    }}
                                />
                            </form>
                            {changeCard && (
                                <div style={{lineHeight: '24px', marginTop: '10px'}}><CircularProgress color="secondary"
                                                                                                       size={24}
                                                                                                       style={{
                                                                                                           verticalAlign: 'middle',
                                                                                                           marginRight: '10px'
                                                                                                       }}/>{getString('SAVING_TEXT_MESSAGE')}
                                </div>)}
                            {!changeCard && (
                                <div style={{
                                    lineHeight: '24px',
                                    marginTop: '10px'
                                }}>{getString('SAVED_TEXT_MESSAGE')}</div>)}
                        </Grid>

                        <Grid item xs={12}>
                            <h3 className="fieldSet">{getString('DOCUMENTS_HEADER')}</h3>
                        </Grid>

                        <Grid item xs={12}>
                            <div>
                                <Button variant="contained" color="primary" onClick={() => {
                                    this.openDocumentDialog(-1)
                                }}>
                                    <AddIcon/>
                                    {getString('ADD_DOCUMENT')}
                                </Button>
                            </div>

                            {this.renderDocumentTable()}

                            {dialogOpen && this.renderDocumentDialog()}
                        </Grid>

                    </Grid>}
            </div>
        )
    }
}

CorporateCardPage.propTypes = {
    auth: PropTypes.object,
    parent: PropTypes.object.isRequired
}

export default CorporateCardPage
